import * as React from "react";
import { Stack, useTheme, useMediaQuery } from "@mui/material";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import Box from "@mui/material/Box";
import { useLocation } from "react-router-dom";
import { FaRegEye } from "react-icons/fa";
import { IoMdEyeOff } from "react-icons/io";
import { formatDate, formatDateString } from "utilities/date";
import { currencyFormatter } from "utilities/currencyFormatter";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: "#f1f1f5",
		color: "#000",
		fontSize: 15,
		fontWeight: 700,
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 16,
	},
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(odd)": {
		backgroundColor: "white",
	},
	// // hide last border
	// "&:last-child td, &:last-child th": {
	// 	border: 0,
	// },
}));


function TablePaginationActions(props) {
	const theme = useTheme();
	const { count, page, rowsPerPage, onPageChange } = props;

	const handleFirstPageButtonClick = (event) => {
		onPageChange(event, 0);
	};

	const handleBackButtonClick = (event) => {
		onPageChange(event, page - 1);
	};

	const handleNextButtonClick = (event) => {
		onPageChange(event, page + 1);
	};

	const handleLastPageButtonClick = (event) => {
		onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
	};

	return (
		<Box sx={{ flexShrink: 0, ml: 2.5 }}>
			<IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
				{theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
			</IconButton>
			<IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
				{theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
			</IconButton>
			<IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
				{theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
			</IconButton>
			<IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
				{theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
			</IconButton>
		</Box>
	);
}

export default function LandlordPaymentsTable(props) {
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(5);
	const [showDetails, setShowDetails] = React.useState(false);
	const [activeID, setActiveID] = React.useState(null);

	const isNonMobileScreen = useMediaQuery("(min-width:600px)");

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const toggleDetailsView = (rowId) => {
		setActiveID(rowId);
		setShowDetails(!showDetails);
	};

	return (
		<>
			<TableContainer component={Paper}>
				<Table aria-label="customized table">
					<TableHead>
						<TableRow>
							<StyledTableCell><span>Amount Paid</span></StyledTableCell>
							<StyledTableCell align="left" sx={{ display: !isNonMobileScreen ? "none": null }}>Month Paid For</StyledTableCell>
							<StyledTableCell align="left" sx={{ display: !isNonMobileScreen ? "none": null }}>Date Paid</StyledTableCell>
							<StyledTableCell align="left">Property Paid For</StyledTableCell>
							<StyledTableCell align="left" sx={{ display: !isNonMobileScreen ? "none": null }}>Paid By</StyledTableCell>
							{!isNonMobileScreen ? <StyledTableCell align="left">Actions</StyledTableCell> : null}
						</TableRow>
					</TableHead>
					<TableBody>
						{(props.rows.length > 0 ? props.rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
							<StyledTableRow key={row.id}>
								<StyledTableCell component="th" scope="row">
									<span>{currencyFormatter(parseInt(row.amount))}</span>
									<Stack spacing={1} style={{ fontSize: "16px", marginTop: "7px" }}>
										{activeID === row.id && showDetails ? (
											<>
												<span>
													<strong>Month Paid For:</strong> {row.related_month.month} {row.related_month.year}
												</span>
												<span>
													<strong>Date Paid:</strong> {row.date_paid}
												</span>
											</>
										) : null}
									</Stack>
								</StyledTableCell>
								<StyledTableCell align="left" sx={{ display: !isNonMobileScreen ? "none": null }}>{row.related_month.month} {row.related_month.year}</StyledTableCell>
								<StyledTableCell align="left" sx={{ display: !isNonMobileScreen ? "none": null }}>{row.date_paid}</StyledTableCell>
								<StyledTableCell align="left">{row.related_landlord_rental.rental_name}</StyledTableCell>
								<StyledTableCell align="left" sx={{ display: !isNonMobileScreen ? "none": null }}>{row.paid_by.first_name} {row.paid_by.last_name}</StyledTableCell>
								{!isNonMobileScreen ? 
									<StyledTableCell align="left">
										<span style={{ cursor: "pointer", display: "flex", alignItems: "center", fontSize: "15px" }} onClick={() => toggleDetailsView(row.id)}>
											See{" "}
											{activeID === row.id && showDetails ? (
												<>
													{" "}
													less <IoMdEyeOff style={{ marginLeft: "5px", marginTop: "1px" }} />{" "}
												</>
											) : (
												<>
													{" "}
													more <FaRegEye style={{ marginLeft: "5px", marginTop: "1px" }} />{" "}
												</>
											)}
										</span>
									</StyledTableCell> 
								:null}
							</StyledTableRow>
						)):(
							<StyledTableRow>
								<StyledTableCell colSpan={6}>No Data Availalble</StyledTableCell>
							</StyledTableRow>
						))}
					</TableBody>
				</Table>
				<TablePagination
					component="div"
					colSpan={12}
					count={props.rows.length}
					page={page}
					onPageChange={handleChangePage}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					labelRowsPerPage="Rows per page" // Custom label for "Rows per page"
					sx={{
						// Styles for the TablePagination component itself
						"& .MuiInputBase-root.MuiInputBase-colorPrimary.MuiTablePagination-input.css-baf1rs-MuiInputBase-root-MuiTablePagination-select": {
							marginRight: "5px",
						},
						"& .MuiTablePagination-actions": {
							marginLeft: 0,
						},
						"& .MuiTablePagination-displayedRows": {
							fontSize: "1rem",
							margin: 0,
						},
						"& .MuiSelect-select.MuiInputBase-input": {
							fontSize: "1rem",
							marginTop: "10px",
						},
						"& .MuiTablePagination-selectLabel": {
							fontSize: "1rem",
						},
						"& .MuiInputBase-root-MuiTablePagination-select .MuiTablePagination-select": {
							marginTop: "4px",
						},
					}}
				/>
			</TableContainer>
		</>
	);
}
